/**
 * 学校类型枚举
 */
export var SchoolType;
(function (SchoolType) {
    SchoolType["KINDERGARTEN"] = "KINDERGARTEN";
    SchoolType["PRIMARY"] = "PRIMARY";
    SchoolType["JUNIOR"] = "JUNIOR";
    SchoolType["NINE_YEAR"] = "NINE_YEAR";
    SchoolType["SENIOR"] = "SENIOR";
    SchoolType["VOCATIONAL"] = "VOCATIONAL";
    SchoolType["COLLEGE"] = "COLLEGE";
    SchoolType["UNIVERSITY"] = "UNIVERSITY";
    SchoolType["GRADUATE"] = "GRADUATE";
    SchoolType["OTHER"] = "OTHER";
})(SchoolType || (SchoolType = {}));
/**
 * 各学校类型对应的年级预设
 */
export const SCHOOL_TYPE_GRADE_MAP = {
    [SchoolType.KINDERGARTEN]: {
        duration: 3,
        label: '幼儿园',
        labelEn: 'Kindergarten',
        gradeLabels: ['小班', '中班', '大班'],
        gradeLabelsEn: ['Junior', 'Middle', 'Senior'],
    },
    [SchoolType.PRIMARY]: {
        duration: 6,
        label: '小学',
        labelEn: 'Primary School',
        gradeLabels: ['一年级', '二年级', '三年级', '四年级', '五年级', '六年级'],
        gradeLabelsEn: ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6'],
    },
    [SchoolType.JUNIOR]: {
        duration: 3,
        label: '初中',
        labelEn: 'Junior High School',
        gradeLabels: ['初一', '初二', '初三'],
        gradeLabelsEn: ['Grade 7', 'Grade 8', 'Grade 9'],
    },
    [SchoolType.NINE_YEAR]: {
        duration: 9,
        label: '九年一贯制',
        labelEn: 'Nine-Year School',
        gradeLabels: ['一年级', '二年级', '三年级', '四年级', '五年级', '六年级', '初一', '初二', '初三'],
        gradeLabelsEn: ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9'],
    },
    [SchoolType.SENIOR]: {
        duration: 3,
        label: '高中',
        labelEn: 'Senior High School',
        gradeLabels: ['高一', '高二', '高三'],
        gradeLabelsEn: ['Grade 10', 'Grade 11', 'Grade 12'],
    },
    [SchoolType.VOCATIONAL]: {
        duration: 3,
        label: '职业学校',
        labelEn: 'Vocational School',
        gradeLabels: ['一年级', '二年级', '三年级'],
        gradeLabelsEn: ['Year 1', 'Year 2', 'Year 3'],
    },
    [SchoolType.COLLEGE]: {
        duration: 3,
        label: '大专',
        labelEn: 'College',
        gradeLabels: ['大一', '大二', '大三'],
        gradeLabelsEn: ['Year 1', 'Year 2', 'Year 3'],
    },
    [SchoolType.UNIVERSITY]: {
        duration: 4,
        label: '大学',
        labelEn: 'University',
        gradeLabels: ['大一', '大二', '大三', '大四'],
        gradeLabelsEn: ['Year 1', 'Year 2', 'Year 3', 'Year 4'],
    },
    [SchoolType.GRADUATE]: {
        duration: 3,
        label: '研究生',
        labelEn: 'Graduate School',
        gradeLabels: ['研一', '研二', '研三'],
        gradeLabelsEn: ['Master Year 1', 'Master Year 2', 'Master Year 3'],
    },
    [SchoolType.OTHER]: {
        duration: 0,
        label: '其他',
        labelEn: 'Other',
        gradeLabels: [],
        gradeLabelsEn: [],
    },
};
/**
 * 根据学校类型和入学年份计算当前年级
 *
 * @param schoolType - 学校类型
 * @param enrollmentYear - 入学年份
 * @param schoolGrades - 自定义年级标签（优先使用）
 * @param locale - 语言 'zh' | 'en'，默认 'zh'
 * @returns 当前年级标签，超出范围返回 '已毕业'/'Graduated'，无法计算返回 null
 */
export function computeCurrentGrade(schoolType, enrollmentYear, schoolGrades, locale = 'zh') {
    // 优先使用自定义年级标签
    if (schoolGrades && schoolGrades.length > 0 && enrollmentYear !== null) {
        const currentYear = new Date().getFullYear();
        const diff = currentYear - enrollmentYear;
        if (diff >= schoolGrades.length) {
            return locale === 'zh' ? '已毕业' : 'Graduated';
        }
        if (diff >= 0 && diff < schoolGrades.length) {
            return schoolGrades[diff];
        }
        return null;
    }
    // 使用预设年级映射
    if (!schoolType)
        return null;
    const config = SCHOOL_TYPE_GRADE_MAP[schoolType];
    if (!config || config.duration === 0)
        return null;
    if (enrollmentYear === null)
        return null;
    const currentYear = new Date().getFullYear();
    const diff = currentYear - enrollmentYear;
    if (diff >= config.duration) {
        return locale === 'zh' ? '已毕业' : 'Graduated';
    }
    if (diff >= 0 && diff < config.duration) {
        return locale === 'zh' ? config.gradeLabels[diff] : config.gradeLabelsEn[diff];
    }
    return null;
}
/**
 * 根据年级标签推断学校类型
 *
 * @param grade - 年级字符串，如 "高一", "初一", "三年级"
 * @returns 匹配的 SchoolType，无法推断时返回 null
 */
export function inferSchoolTypeFromGrade(grade) {
    if (!grade || !grade.trim())
        return null;
    const normalized = grade.trim();
    // Priority order: more specific labels checked first
    const priorityOrder = [
        SchoolType.KINDERGARTEN,
        SchoolType.SENIOR,
        SchoolType.JUNIOR,
        SchoolType.NINE_YEAR,
        SchoolType.PRIMARY,
        SchoolType.UNIVERSITY,
        SchoolType.COLLEGE,
        SchoolType.GRADUATE,
        SchoolType.VOCATIONAL,
        SchoolType.OTHER,
    ];
    for (const schoolType of priorityOrder) {
        const config = SCHOOL_TYPE_GRADE_MAP[schoolType];
        if (config.gradeLabels.some((label) => normalized.includes(label))) {
            return schoolType;
        }
    }
    return null;
}
//# sourceMappingURL=school.js.map